/*
 * ThemeContext — dual-theme palette switcher.
 *
 * Default: "dark" (Deep Navy Cyber theme). Persisted in localStorage under
 * `velox-theme`. Sets `data-velox-theme="dark|light"` on <html> so global
 * CSS variables in App.css can flip the palette.
 *
 * Palette (per product spec):
 *   Dark theme:
 *     bg              #0b132b   — Deep Navy
 *     panel           #1c2541   — Dark Slate Gray
 *     accent-cyan     #00b4d8   — Electric Cyan
 *     accent-blue     #0077b6   — Digital Blue
 *     accent-emerald  #06d6a0   — Emerald Green (actions)
 *     accent-mint     #2ec4b6   — Neo-Mint (highlights)
 *   Light theme:
 *     bg              #f7f9fc
 *     panel           #ffffff
 *     accent-cyan     #0077b6
 *     accent-blue     #023e8a
 *     accent-emerald  #06a373
 *     accent-mint     #14b8a6
 */

import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({ theme: "dark", toggle: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      return window.localStorage.getItem("velox-theme") || "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-velox-theme", theme);
    try {
      window.localStorage.setItem("velox-theme", theme);
    } catch { /* storage disabled — safe to ignore */ }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
